import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPaginatedTable from './ServerPaginatedTable';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import MobileHeader from './MobileHeader';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { fetchUsersPaginated, invalidateUsersCache, updateUserInCache, removeUserFromCache, updateSalesRepsInCache } from './usersManagementSlice';
import PageHeader from './PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faCheck, faExclamation, faTrash, faPaperPlane, faEye, faSync } from '@fortawesome/free-solid-svg-icons';
import SalesRepModal from './SalesRepModal';
import LogsModal from './showLogsModal';

const UserManager = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const primaryColor = theme.primary;
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    // Redux state
    const {
        data: users,
        salesReps,
        pagination,
        loading,
        error: reduxError
    } = useSelector((state) => state.usersManagement.paginatedUsers);

    // URL-based state with defaults
    const currentPage = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 50;
    const sortField = searchParams.get('sortField') || 'FirstName';
    const sortDirection = searchParams.get('sortDirection') || 'ASC';
    const serverSearch = searchParams.get('search') || '';
    const roleFilter = searchParams.get('roleFilter') || '';
    const setupFilter = searchParams.get('setupFilter') || '';

    // Local UI state
    const [successMessage, setSuccessMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState(serverSearch);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [logs, setLogs] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalAction, setModalAction] = useState(null);

    const apiUrl = process.env.REACT_APP_API_URL;

    // Helper function to update URL parameters
    const updateSearchParams = useCallback((updates) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);

            Object.entries(updates).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') {
                    newParams.delete(key);
                } else {
                    newParams.set(key, value.toString());
                }
            });

            // Remove default values to keep URL clean
            if (newParams.get('page') === '1') newParams.delete('page');
            if (newParams.get('pageSize') === '50') newParams.delete('pageSize');
            if (newParams.get('sortField') === 'FirstName') newParams.delete('sortField');
            if (newParams.get('sortDirection') === 'ASC') newParams.delete('sortDirection');

            return newParams;
        });
    }, [setSearchParams]);

    // Memoized fetch function
    const fetchData = useCallback((params = {}) => {
        const fetchParams = {
            userId: currentUser?.userId,
            page: params.page || currentPage,
            pageSize: params.pageSize || pageSize,
            sortField: params.sortField || sortField,
            sortDirection: params.sortDirection || sortDirection,
            search: params.search !== undefined ? params.search : serverSearch,
            roleFilter: params.roleFilter || roleFilter,
            setupFilter: params.setupFilter || setupFilter,
            forceRefresh: params.forceRefresh || false
        };

        if (fetchParams.userId) {
            dispatch(fetchUsersPaginated(fetchParams));
        }
    }, [
        dispatch,
        currentUser?.userId,
        currentPage,
        pageSize,
        sortField,
        sortDirection,
        serverSearch,
        roleFilter,
        setupFilter
    ]);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (serverSearch !== searchQuery) {
                updateSearchParams({
                    search: searchQuery,
                    page: 1 // Reset to page 1 when searching
                });
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, serverSearch, updateSearchParams]);

    // Sync searchQuery with URL parameter
    useEffect(() => {
        setSearchQuery(serverSearch);
    }, [serverSearch]);

    // Fetch data when dependencies change
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Check for user update flag and refresh cache if needed
    useEffect(() => {
        const checkForUserUpdates = () => {
            const userUpdateFlag = sessionStorage.getItem('userUpdated');
            if (userUpdateFlag) {
                console.log('🔄 User update detected - refreshing cache');
                // Clear the flag first
                sessionStorage.removeItem('userUpdated');
                // Invalidate cache and refresh
                dispatch(invalidateUsersCache());
                fetchData({ forceRefresh: true });
            }
        };

        // Check immediately when component mounts
        checkForUserUpdates();

        // Also check when window regains focus (user returns from edit page)
        const handleFocus = () => {
            checkForUserUpdates();
        };

        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [dispatch, fetchData]);

    const handleSort = (field, direction) => {
        updateSearchParams({
            sortField: field,
            sortDirection: direction,
            page: 1 // Reset to page 1 when sorting
        });
    };

    const handlePageChange = (newPage) => {
        updateSearchParams({ page: newPage });
    };

    const handlePageSizeChange = (newPageSize) => {
        updateSearchParams({
            pageSize: newPageSize,
            page: 1 // Reset to page 1 when changing page size
        });
    };

    const handleFilterChange = (filterType, value) => {
        const updates = { page: 1 };

        switch (filterType) {
            case 'role':
                updates.roleFilter = value;
                break;
            case 'setup':
                updates.setupFilter = value;
                break;
        }

        updateSearchParams(updates);
    };

    // User action handlers
    const handleEditClick = (row) => {
        navigate(`/EditUserForm/${row.RecordId}`, { state: { rowData: row } });
    };

    const handleAssignClick = (user) => {
        setSelectedUser(user);
        setShowAssignModal(true);
    };

    const fetchUserLogs = async (userId) => {
        try {
            const response = await fetch(`${apiUrl}/dataServer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'getUserAuditLogsById',
                    payload: {
                        requestedUserId: userId,
                        userId: currentUser.userId
                    }
                }),
            });

            if (response.ok) {
                const result = await response.json();
                setLogs(result.logs);
                setShowLogsModal(true);
            } else {
                setSuccessMessage(`Error: ${await response.text()}`);
            }
        } catch (error) {
            setSuccessMessage(`Error fetching logs: ${error.message}`);
        }
    };

    const openModal = (action, row) => {
        setModalAction(action);
        setSelectedUser(row);
        setShowModal(true);
    };

    // Sales rep management handlers
    const handleAssignRep = async (repId) => {
        try {
            const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: selectedUser.UserId,
                    repId: repId,
                    requestType: 'assignSalesRep'
                }),
            });

            if (response.ok) {
                const assignedRep = salesReps.find(rep => rep.Id === repId);
                // Update user in cache
                dispatch(updateUserInCache({
                    userId: selectedUser.UserId,
                    updatedUser: { SalesRepId: repId, salesRepInfo: assignedRep }
                }));
                setShowAssignModal(false);
                setSuccessMessage('Sales rep assigned successfully.');
            } else {
                setSuccessMessage(`Server Error: ${await response.text()}`);
            }
        } catch (error) {
            setSuccessMessage(`Error: ${error.message}`);
        }
    };

    const handleAddRep = async (salesRep) => {
        try {
            const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    requestType: 'addSalesRep',
                    subDomain: currentUser.subDomain,
                    salesRep: salesRep,
                    userId: currentUser.userId
                }),
            });

            if (response.ok) {
                const result = await response.json();
                const newSalesReps = [...salesReps, result.salesRep];
                dispatch(updateSalesRepsInCache(newSalesReps));
                setSuccessMessage('Sales rep added successfully.');
            } else {
                const errorText = await response.text();
                setSuccessMessage(`Error adding sales rep: ${errorText}`);
            }
        } catch (error) {
            setSuccessMessage(`Error: ${error.message}`);
        }
    };

    const handleEditRep = async (salesRep) => {
        try {
            const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    requestType: 'editSalesRep',
                    salesRep: salesRep,
                    userId: currentUser.userId
                }),
            });

            if (response.ok) {
                const updatedSalesReps = salesReps.map(rep =>
                    rep.Id === salesRep.Id ? salesRep : rep
                );
                dispatch(updateSalesRepsInCache(updatedSalesReps));

                // Update all users who have this sales rep
                users.forEach(user => {
                    if (user.SalesRepId === salesRep.Id) {
                        dispatch(updateUserInCache({
                            userId: user.UserId,
                            updatedUser: { salesRepInfo: salesRep }
                        }));
                    }
                });
                setSuccessMessage('Sales rep updated successfully.');
            } else {
                const errorText = await response.text();
                setSuccessMessage(`Error editing sales rep: ${errorText}`);
            }
        } catch (error) {
            setSuccessMessage(`Error: ${error.message}`);
        }
    };

    const handleDeleteRep = async (repToDelete, replaceWith) => {
        try {
            const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/getAdminUserData', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    salesRepId: repToDelete.Id,
                    replaceWith: replaceWith,
                    requestType: 'deleteSalesRep'
                }),
            });

            if (response.ok) {
                const updatedSalesReps = salesReps.filter(rep => rep.Id !== repToDelete.Id);
                dispatch(updateSalesRepsInCache(updatedSalesReps));

                // Update all users who had this sales rep
                users.forEach(user => {
                    if (user.SalesRepId === repToDelete.Id) {
                        const newSalesRepInfo = replaceWith ? salesReps.find(rep => rep.Id === replaceWith) : null;
                        dispatch(updateUserInCache({
                            userId: user.UserId,
                            updatedUser: {
                                SalesRepId: replaceWith,
                                salesRepInfo: newSalesRepInfo
                            }
                        }));
                    }
                });
                setSuccessMessage('Sales rep deleted successfully.');
            } else {
                const errorText = await response.text();
                setSuccessMessage(`Error deleting sales rep: ${errorText}`);
            }
        } catch (error) {
            setSuccessMessage(`Error: ${error.message}`);
        }
    };

    const confirmModalAction = async () => {
        if (!selectedUser) return;
        setShowModal(false);

        try {
            const requestBody = {
                action: modalAction === 'delete' ? 'deleteUser' : 'resendInvite',
                payload: {
                    targetUserId: selectedUser.UserId,
                    requestingUserId: currentUser.userId,
                    invitationToken: selectedUser.InvitationToken
                },
            };

            const response = await fetch(`${apiUrl}/dataServer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            const responseData = await response.json();

            if (!responseData.success) {
                setSuccessMessage(`${responseData.message}`);
                return;
            }

            if (modalAction === 'delete') {
                // Remove the deleted user from cache
                dispatch(removeUserFromCache(selectedUser.UserId));
                setSuccessMessage('Successfully deleted user.');
                // Set flag for other tabs/windows that might have user list open
                sessionStorage.setItem('userUpdated', 'true');
                // Invalidate cache and refresh to get updated pagination
                dispatch(invalidateUsersCache());
                fetchData({ forceRefresh: true });
            } else {
                // Update user with new invitation token
                dispatch(updateUserInCache({
                    userId: selectedUser.UserId,
                    updatedUser: { InvitationToken: responseData.invitationToken }
                }));
                setSuccessMessage('Successfully resent invitation.');
                // Set flag for other tabs/windows that might have user list open
                sessionStorage.setItem('userUpdated', 'true');
            }
        } catch (error) {
            setSuccessMessage(`Error: ${error.message}`);
        }
    };

    const clearSuccessMessage = () => setSuccessMessage("");

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 7000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const columns = useMemo(() => [
        {
            name: 'Name',
            selector: row => `${row.FirstName} ${row.LastName}`,
            sortable: true,
            sortField: 'FirstName',
            reorder: true,
            style: { whiteSpace: 'nowrap' }
        },
        {
            name: 'Company Name',
            selector: row => row.CompanyName,
            sortable: true,
            sortField: 'CompanyName',
            reorder: true,
            style: { whiteSpace: 'nowrap' }
        },
        {
            name: 'Email',
            selector: row => row.Email,
            sortable: true,
            sortField: 'Email',
            reorder: true,
            style: { whiteSpace: 'nowrap' }
        },
        {
            name: 'Phone',
            selector: row => row.Phone,
            sortable: true,
            sortField: 'Phone',
            reorder: true,
            style: { whiteSpace: 'nowrap' }
        },
        {
            name: 'Location',
            selector: row => row.latestAgreement ? row.latestAgreement.LocationCityState : 'N/A',
            sortable: false,
            reorder: true,
            style: { whiteSpace: 'nowrap' }
        },
        {
            name: 'Setup Complete',
            cell: row => row.SetupComplete ? (
                <FontAwesomeIcon icon={faCheck} className="text-green-500" />
            ) : (
                <FontAwesomeIcon icon={faExclamation} className="text-red-500" />
            ),
            ignoreRowClick: true,
            style: { width: '50px', whiteSpace: 'nowrap' }
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="flex space-x-3">
                    {/* Edit */}
                    <FontAwesomeIcon
                        icon={faEdit}
                        className="themedIcon2xl cursor-pointer"
                        title="Edit User"
                        onClick={() => handleEditClick(row)}
                    />
                    {/* Resend Invite */}
                    <FontAwesomeIcon
                        icon={faPaperPlane}
                        className={`themedIcon2xl ${row.UserId !== 'Pending' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title='Resend Invite'
                        onClick={() => {
                            if (row.UserId === 'Pending') {
                                openModal('resend', row);
                            }
                        }}
                    />
                    {/* Delete */}
                    <FontAwesomeIcon
                        icon={faTrash}
                        className={`themedIcon2xl ${row.UserId === currentUser.userId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={row.UserId === currentUser.userId ? 'You cannot delete yourself' : 'Delete User'}
                        onClick={() => {
                            if (row.UserId !== currentUser.userId) {
                                openModal('delete', row);
                            }
                        }}
                    />
                    {/* View Logs */}
                    <FontAwesomeIcon
                        icon={faEye}
                        className="themedIcon2xl cursor-pointer"
                        title="View Logs"
                        onClick={() => fetchUserLogs(row.UserId)}
                    />
                </div>
            ),
            ignoreRowClick: true,
        }
    ], [handleEditClick, currentUser.userId]);

    const totalPages = pagination?.totalPages || 0;
    const totalCount = pagination?.totalCount || 0;

    return (
        <>
            <MobileHeader theme={theme} />
            <div className='mobile-container'>
                <PageHeader
                    heading="User Management"
                    context="Manage and administer user accounts and permissions."
                />
                {reduxError && <div className="alert alert-danger">{reduxError}</div>}

                {/* Advanced Filters Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters & Search</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search users, email, company..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {loading && searchQuery && (
                                <div className="text-xs text-gray-500 mt-1">Searching...</div>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role Filter</label>
                            <select
                                value={roleFilter}
                                onChange={(e) => handleFilterChange('role', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">All Roles</option>
                                <option value="True">Admins</option>
                                <option value="False">Users</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Setup Status</label>
                            <select
                                value={setupFilter}
                                onChange={(e) => handleFilterChange('setup', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">All Status</option>
                                <option value="complete">Setup Complete</option>
                                <option value="incomplete">Setup Incomplete</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Summary */}
                {pagination && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4 flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                            Total: {totalCount} users
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => fetchData({ forceRefresh: true })}
                                className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center space-x-1"
                                disabled={loading}
                                title="Refresh data"
                            >
                                <FontAwesomeIcon
                                    icon={faSync}
                                    className={`text-xs ${loading ? 'animate-spin' : ''}`}
                                />
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Server-Side Paginated Table */}
                <ServerPaginatedTable
                    columns={columns}
                    data={users || []}
                    primaryColor={primaryColor}
                    loading={loading}
                    alertMessage={successMessage}
                    clearAlert={clearSuccessMessage}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSort={handleSort}
                    pageSizeOptions={[25, 50, 100]}
                    children={
                        <Link className="btn-base btn-primary" to="/adduser">
                            ➕ Add New User
                        </Link>
                    }
                />

                {/* Sales Rep Modal */}
                <SalesRepModal
                    show={showAssignModal}
                    onClose={() => setShowAssignModal(false)}
                    onAdd={handleAddRep}
                    onAssign={handleAssignRep}
                    currentUser={currentUser}
                    selectedUser={selectedUser}
                    salesReps={salesReps}
                    setSalesReps={() => {}} // Not needed, handled by Redux
                    onEdit={handleEditRep}
                    onDelete={handleDeleteRep}
                    setUsers={() => {}} // Not needed, handled by Redux
                />

                {/* Logs Modal */}
                {showLogsModal && (
                    <LogsModal
                        logs={logs}
                        onClose={() => setShowLogsModal(false)}
                    />
                )}

                {/* Confirmation Modal */}
                {showModal && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white rounded-lg p-6 w-96">
                            <h2 className="text-xl font-bold mb-4">
                                {modalAction === 'delete' ? 'Delete User?' : 'Resend Invitation?'}
                            </h2>
                            <p className="mb-4">
                                {modalAction === 'delete'
                                    ? `Are you sure you want to delete "${selectedUser?.Email}"? This action can't be undone.`
                                    : `Resend the invite to "${selectedUser?.Email}"?`}
                            </p>
                            <div className="flex justify-end space-x-2">
                                <button
                                    className="px-4 py-2 bg-gray-300 rounded"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-base btn-primary"
                                    onClick={confirmModalAction}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default UserManager;